import { submitFormJson } from 'widgets/toolbox/ajax';
import { TInputRadioInstance } from 'widgets/forms/InputRadio';
import { TRadioSelectorInstance } from 'widgets/checkout/RadioSelector';
import { TBasketModel } from 'types/models/order';
import { TBasicInputInstance } from 'widgets/forms/BasicInput';

declare const narvar: (action: string, options: object) => void;

type TShippingMethod = {
    isDateTimeSelectable: boolean;
    ID: string;
};

/**
 * @param AddressCheckoutForm Base widget for extending
 * @returns Shipping Form widget
 */
function ShippingMethodFormClassCreator(AddressCheckoutForm: ReturnType<typeof import('widgets/checkout/AddressCheckoutForm').default>) {
    class CoreShippingMethodForm extends AddressCheckoutForm {
        prevShippingData = '';

        selectedShippingMethod: TShippingMethod | null = null;

        prefs() {
            return {
                updateShippingListUrl: '',
                selectShippingMethodUrl: '',
                invalidClass: 'm-invalid',
                ...super.prefs()
            };
        }

        /**
         * @description Update internal order object
         * @param params - Object with necessary data for activation
         * @param params.order - Order object
         * @param params.customer - Customer object
         */
        activate({ order, customer }) {
            this.order = order;
            this.selectedShippingMethod = this.order?.shipping?.[0]?.selectedShippingMethod || null;

            this.customer = customer;
        }

        /**
         * @description Select shipping method handler
         * @param inputRadio - Shipping method's input
         * @listens InputRadio#change
         * @emits ShippingMethodForm#selectshippingmethod
         */
        selectShippingMethod(inputRadio: TInputRadioInstance) {
            inputRadio.ref('self').get()?.setAttribute('aria-busy', 'true');
            this.emit('selectshippingmethod', inputRadio, this);

            this.selectedShippingMethod =
                this.order?.shipping?.[0]?.applicableShippingMethods?.find(method => {
                    return method.ID === inputRadio.getDataValue();
                }) || null;

            this.ref('shippingDateTimeSlotSelection').toggle(this.selectedShippingMethod?.isDateTimeSelectable);
        }

        /**
         * @description Display info message in case if user's shipment was automatically changed
         * @param isShipmentChanged - marker to define if shipment was auto-changed
         */
        handleShipmentChangedMsg(isShipmentChanged: boolean) {
            this.ref('shipmentMessageLabel').toggle(isShipmentChanged);
        }

        /**
         * @description Update shipping methods list
         * @emits ShippingForm#updateshippingform
         * @param shippingForm - Shipping form widget
         * @returns Promise object represents server response for shipping methods updating
         */
        updateShippingState(shippingForm): ReturnType<typeof submitFormJson> | Promise<null> {
            const dataToSend = shippingForm.getFormFieldsShort();
            // Stringify data and if they not change from previous request
            // Skip sending request
            const stringifiedData = JSON.stringify(dataToSend);

            if (stringifiedData === this.prevShippingData) {
                return Promise.resolve(null);
            }

            this.prevShippingData = stringifiedData;

            this.busy();

            return submitFormJson(this.prefs().updateShippingListUrl, dataToSend)
                .then(response => {
                    if (response?.order) {
                        this.handleShipmentChangedMsg(response.isShipmentAutoChanged);
                        this.updateOrder(response.order);
                        this.updateState(response.order);
                        /**
                         * @description Event to update summary block
                         * @event ShippingForm#updateshippingform
                         */
                        this.emit('updateshippingform', response.order);
                    }

                    return response;
                })
                .finally(() => {
                    this.unbusy();
                });
        }

        /**
         * @description Re-render shipping methods list
         * @param basket - Basket object
         */
        updateState(basket: TBasketModel) {
            this.getById('shippingMethodList', (shippingMethodList: TRadioSelectorInstance) => {
                return shippingMethodList.updateState(basket).then(() => {
                    const narvarContainer = shippingMethodList.ref('narvarEddMessageContainer')?.get();

                    if (narvarContainer) {
                        narvar('getEdd', {});
                    }
                });
            });
        }

        /**
         * @description Submit form handler
         */
        async submitAsync() {
            const applicableShippingMethodsError = this.order?.shipping?.[0]?.applicableShippingMethodsError;

            if (applicableShippingMethodsError) {
                return Promise.reject(new Error(applicableShippingMethodsError));
            }

            if (this.isValid()) {
                const result = await this.handleSubmit();

                if (!result?.success) {
                    return Promise.reject(new Error('Server response is not successful'));
                }

                return Promise.resolve(result);
            }

            this.showAddressForm();
            this.validate();

            return Promise.reject(new Error('Validation failed'));
        }

        isDateSelectionValid(): boolean {
            if (this.selectedShippingMethod?.isDateTimeSelectable) {
                const pickupDate = this.getSelectedPickupDate();

                if (!pickupDate) {
                    return false;
                }
            }

            return true;
        }

        /**
         * @description Get form field instances
         * Prevents deliveryTimeCode from validation in case if selected shipping method
         * doesn't support date/time selection
         * @returns Array of form field instances
         */
        getFields(): Array<TBasicInputInstance> {
            const fields = super.getFields();

            if (this.selectedShippingMethod?.isDateTimeSelectable) {
                return fields;
            }

            return fields.filter(item => {
                return item?.id !== 'deliveryTimeCode';
            });
        }

        /**
         * @description Check is Form valid based on Form fields validation
         * @returns boolean value is Form valid
         */
        isValid(): boolean {
            return super.isValid() && this.isDateSelectionValid();
        }

        /**
         * @description Form validate
         * @returns boolean value is Form input valid
         */
        validate(): boolean {
            const isFormValid = super.validate();

            const isDateSelectionValid = this.isDateSelectionValid();

            this.ref('shippingDateTimeSlotSelection').toggleClass(this.prefs().invalidClass, !isDateSelectionValid);

            return isFormValid && isDateSelectionValid;
        }

        /**
         * @description Handle date selection event
         */
        dateSelected() {
            this.ref('shippingDateTimeSlotSelection').toggleClass(this.prefs().invalidClass, false);
        }

        /**
         * @description Get selected pickup date
         * @returns Selected pickup date or null
         */
        getSelectedPickupDate(): { value: string; name: string } | null {
            let pickupDate: { value: string; name: string } | null = null;

            this.getById('selectDateTimeSlotCalendar', dateTimeSlotCalendar => {
                const value = dateTimeSlotCalendar.ref('pickupDate')?.val();
                const name = (dateTimeSlotCalendar.ref('pickupDate')?.get() as HTMLInputElement)?.name;

                if (value && name) {
                    pickupDate = { value, name };
                }
            });

            return pickupDate;
        }

        /**
         * @description Get Form fields
         * @returns Object contains form fields
         */
        getFormFields(): { [key: string]: string | undefined } {
            const fields = super.getFormFields();

            const pickupDate = this.getSelectedPickupDate();

            if (pickupDate) {
                fields[pickupDate.name] = pickupDate.value;
            }

            const pickupTime = this.ref('timeSlotSelect')?.val();

            if (pickupTime) {
                fields.pickupTime = pickupTime;
            }

            return fields;
        }
    }

    return CoreShippingMethodForm;
}

export type TShippingMethodForm = ReturnType<typeof ShippingMethodFormClassCreator>;

export type TShippingMethodFormInstance = InstanceType<TShippingMethodForm>;

export default ShippingMethodFormClassCreator;
